/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import {EditorDialog} from '@ion/dls/src/editors/EditorDialog';
import styles from '@ion/dls/src/editors/editorStyles';
import {
  definitionsToState, resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid, IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  AG_CLASSES,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useViewSubGroups} from '@ion/dls/src/hooks/useViewSubGroups';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {showBulkViewGrid} from './state';
import {getDeviceIdentifier} from './ViewDeviceEditorUtils';

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  closeButton : {
    top : '23px'
  },
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`,
    paddingTop   : '20px'
  },
  formTitle : {
    justifyContent : 'left',
    alignItems     : 'center',
    marginLeft     : '10px'
  }
});

const useStyles = makeStyles(() => ({
  devicesGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      maxHeight                : '100vh',
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto',
        maxHeight : '420px'
      }
    }
  }
}));

export interface DeviceGridProps {
  onDevicesSelected: Function;
  onSearchSuccess: Function;
  removeSearch: Function;
  accountId: number
  searchValue: string;
};

/**
 * Grid to be rendered on the Detailed Device popup
 */
export const BulkViewDeviceGrid = ({
  onDevicesSelected,
  onSearchSuccess,
  removeSearch,
  accountId,
  searchValue = ''
}: DeviceGridProps) => {
  const editorStyles                          = useExtendStyles(styles, styleOverrides);
  const classes                               = editorStyles();
  const localClasses                          = useStyles();
  const dispatch                              = useDispatch();
  const ionGridRef                            = useRef<IonGridAPI>();
  const [gridApi, setGridApi]                 = useState(null);
  const [gridItems, setGridItems]             = useState([]);
  const [selectedRows, setSelectedRows]       = useState([]);
  const [viewSubGroups, toggleViewSubGroups]  = useViewSubGroups(true);
  const [refreshRequired, setRefreshRequired] = useState(false);

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const getGridHeight = useCallback(function() {
    return localClasses.devicesGrid;
  }, []);

  const onCloseGrid = useCallback(function(): void {
    dispatch(showBulkViewGrid('', false));
  }, []);

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && Array.isArray(gridItems) && !gridItems.length) {
      gridApi.showNoRowsOverlay();
    }
  }, [gridItems]);

  /**
   * Fetching device based on search text.
   */
  const getDevices = async function({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    try {
      const {devices: items, totalCount} = await loadDevicesBySmartSearch({
        accountId,
        pageId           : page,
        pageSize         : pageSize,
        sortOn           : sort,
        queryString      : `(*${searchValue}*)`,
        includeSubGroups : viewSubGroups as boolean
      });
      const devices = clone(items);
      const results  = {items: devices, totalCount: totalCount};
      callback(results);
      setGridItems(devices);
      return {devices, totalCount};
    } catch (err) {
      error('Error occurred retrieving detailed device data.');
    }
  };

  // Provides reference to the grid's api.
  function onGridReady(api) {
    setGridApi(api);
  };

  function handleRowClicked(event) {
    const rowsSelected = event?.data;
    onSearchSuccess?.(rowsSelected);
    removeSearch?.();
  }

  function onSelectionChanged(event) {
    const rowsSelected = event?.api?.getSelectedRows();
    setSelectedRows(rowsSelected);
  }

  function handleSelect() {
    if (selectedRows?.length) {
      onCloseGrid();
      removeSearch?.();
      onDevicesSelected(selectedRows);
    }
  }

  return (
    <EditorDialog open
      dialogTitle={translate('admin-devices:dialogBulkView.searchResults', {text: searchValue})}
      onCloseDialog={onCloseGrid}
      onCancelDialog={onCloseGrid}
      styleOverrides={styleOverrides}
      onPrimaryButtonClick={handleSelect}>
      <div className={classes.formDialogContent}>
        <IonGrid ref={ionGridRef}
          omitHeader
          paginationEnabled
          showCustomPaginationBar
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          loadItems={getDevices}
          augmentItems={constructBulkViewDeviceGridRows}
          attributeMappings={attributeMappings}
          columnDefinitions={getColumnDefinitions()}
          getGridHeight={getGridHeight}
          pageOffset
          pageSizes={PAGE_SIZES}
          rowMenusEnabled={false}
          defaultSort={defaultSort}
          toggleViewSubGroups={toggleViewSubGroups as Function}
          viewSubGroups={viewSubGroups as boolean}
          gridOptions={{onSelectionChanged}}
          onGridReady={onGridReady} />
      </div>
    </EditorDialog>
  );
};

/**
 * Converts a collection of devices and converts them to a list of items specific
 * for display within the Admin Devices Grid, while retaining all of the items existing
 * attributes,
 */
export const constructBulkViewDeviceGridRows = (devices: Device[]): GridResult[] => {
  attributeMappings.deviceType = devices;
  return devices?.map(device => ({
    ...device,
    deviceId     : device?.id,
    deviceName   : device?.name || getDeviceIdentifier(device),
    deviceType   : device?.type?.title,
    identifierUI : getDeviceIdentifier(device),
    accountUI    : device?.account?.title,
    assetName    : device?.asset?.title,
    statusUI     : translateStatus(device?.status)
  })) as GridResult[];
};

const attributeMappings = {
  deviceId     : 'id',
  deviceName   : 'name',
  identifierUI : 'esn',
  accountUI    : 'account',
  statusUI     : 'status',
  deviceType   : {}
};

/**
 * Returns column definitions for desktop admin devices grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => (
  [{
    headerName : translate('admin-devices:grid.columns.deviceId'),
    field      : 'deviceId',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceName'),
    field      : 'deviceName',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceIdentifier'),
    field      : 'identifierUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceType'),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.asset'),
    field      : 'assetName',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.account'),
    field      : 'accountUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.status'),
    field      : 'statusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }]
);

/**
 * Returns column definitions for mobile admin assets grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => (
  [{
    headerName : translate('admin-devices:grid.columns.deviceId'),
    field      : 'deviceId',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceName'),
    field      : 'deviceName',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceIdentifier'),
    field      : 'identifierUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceType'),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.asset'),
    field      : 'assetName',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.account'),
    field      : 'accountUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.status'),
    field      : 'statusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }]
);

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});

export default memo(BulkViewDeviceGrid);
